import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import product1 from '../../../../assets/images/ecommerce/product-table-1.png';
import { Input, Button, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Table, Spinner, FormGroup, Label, Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import API_BASE_URL from '../../../../Config/api';

const ProductList = () => {
  const [productData, setProductData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pending, setPending] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [collections, setCollections] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState('');

  // Price history modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [actionError, setActionError] = useState('');
  const [catalogueBusy, setCatalogueBusy] = useState(false);

  const openHistory = async (sku) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryProduct(null);
    setHistoryRows([]);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/product-price-history/${encodeURIComponent(sku)}`);
      setHistoryProduct(res.data.product);
      setHistoryRows(res.data.history || []);
    } catch (err) {
      console.error('Error fetching price history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchProducts = async (searchQuery = '') => {
    try {
      setPending(true);
      const url = searchQuery
        ? `${API_BASE_URL}/api/product-list?search=${encodeURIComponent(searchQuery)}`
        : `${API_BASE_URL}/api/product-list`;

      const res = await axios.get(url);
      const products = res.data.products.map(product => {
        // Absolute (Shopify) URLs are used as-is; relative uploads are served
        // from the API origin. Stored paths may or may not start with a slash,
        // and on prod API_BASE_URL is '' — so joining blindly would produce
        // '//uploads/...', which a browser reads as a protocol-relative host.
        const imageUrl = product.image
          ? (/^https?:\/\//i.test(product.image)
              ? product.image
              : `${API_BASE_URL}/${product.image.replace(/^\/+/, '')}`)
          : product1;
        return {
          id: product.id,
          productIds: product.productIds || [product.id],
          image: imageUrl, // Use product image if available, fallback to default
          title: product.title,
          colour: product.colour,
          fabric: product.fabric,
          sizes: product.sizes,
          sku: product.sku,
          collection: product.collection,
          category: product.category,
          wholesale_price: product.wholesale_price,
          wholesale_price_usd: product.wholesale_price_usd,
          wholesale_price_gbp: product.wholesale_price_gbp,
          retail_euro: product.retail_euro,
          hsn_code: product.hsn_code,
          is_active: product.is_active == null ? 1 : Number(product.is_active),
        };
      });
      setProductData(products);
      setPending(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setPending(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/collections`);
      setCollections(res.data.collections || []);
    } catch (err) {
      console.error('Error fetching collections:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCollections();
  }, []);

  // The collection filter is applied on top of whatever the search returned, so
  // the two narrow the list together.
  useEffect(() => {
    setFilteredData(
      collectionFilter
        ? productData.filter(p => (p.collection || '') === collectionFilter)
        : productData
    );
  }, [productData, collectionFilter]);

  const handleSearch = () => {
    fetchProducts(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCollectionFilter('');
    fetchProducts();
  };

  // Real-time search as user types (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        fetchProducts(searchTerm);
      } else if (searchTerm === '') {
        fetchProducts();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Helper function to highlight search terms
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  // ===== Edit =====
  const openEdit = (row) => {
    setActionError('');
    setEditForm({
      id: row.id,
      productIds: row.productIds,
      title: row.title || '',
      sku: row.sku || '',
      colour: row.colour || '',
      fabric: row.fabric || '',
      collection: row.collection || '',
      category: row.category || '',
      hsn_code: row.hsn_code || '',
      wholesale_price_usd: row.wholesale_price_usd ?? '',
      wholesale_price_gbp: row.wholesale_price_gbp ?? '',
      wholesale_price: row.wholesale_price ?? '',
      retail_euro: row.retail_euro ?? '',
    });
    setEditOpen(true);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setSaving(true);
    setActionError('');
    try {
      await axios.put(`${API_BASE_URL}/api/product/${editForm.id}`, {
        productIds: editForm.productIds,
        title: editForm.title,
        sku: editForm.sku,
        colour: editForm.colour,
        fabric: editForm.fabric,
        collection: editForm.collection,
        category: editForm.category,
        hsn_code: editForm.hsn_code,
        wholesale_price_usd: editForm.wholesale_price_usd,
        wholesale_price_gbp: editForm.wholesale_price_gbp,
        wholesale_price: editForm.wholesale_price,
        retail_euro: editForm.retail_euro,
      });
      setEditOpen(false);
      setEditForm(null);
      fetchProducts(searchTerm);
    } catch (err) {
      console.error('Error saving product:', err);
      setActionError(err.response?.data?.error || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ===== Activate / Disable =====
  const toggleStatus = async (row) => {
    const nextActive = row.is_active ? 0 : 1;
    setActionError('');
    try {
      await axios.patch(`${API_BASE_URL}/api/product/${row.id}/status`, {
        productIds: row.productIds,
        is_active: nextActive,
      });
      fetchProducts(searchTerm);
    } catch (err) {
      console.error('Error updating status:', err);
      setActionError('Failed to update product status.');
    }
  };

  // ===== Delete =====
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    try {
      await axios.delete(`${API_BASE_URL}/api/product/${deleteTarget.id}`, {
        data: { productIds: deleteTarget.productIds },
      });
      setDeleteTarget(null);
      fetchProducts(searchTerm);
    } catch (err) {
      console.error('Error deleting product:', err);
      setDeleteTarget(null);
      setActionError(err?.response?.data?.error || 'Failed to delete product.');
    } finally {
      setDeleting(false);
    }
  };

  // ===== Catalogue PDF =====
  // Builds a print-ready lookbook of exactly what's on screen (same search and
  // collection filter), for sending on to stores.
  const downloadCatalogue = async (currency) => {
    // A page per colourway, with every shot on it — a whole collection runs to
    // tens of MB, which is worth a heads-up before the wait.
    if (
      filteredData.length > 100 &&
      !window.confirm(
        `This catalogue covers ${filteredData.length} products — a page each, with every image. ` +
        'It can take a minute to build and the file may run to tens of MB. Continue?'
      )
    ) return;

    setCatalogueBusy(true);
    setActionError('');
    try {
      const params = new URLSearchParams({ currency });
      if (searchTerm) params.set('search', searchTerm);
      if (collectionFilter) params.set('collection', collectionFilter);

      const res = await axios.get(`${API_BASE_URL}/api/product-catalog-pdf?${params.toString()}`, {
        responseType: 'blob',
      });

      // The server names the file after the collection it actually built; fall
      // back to a local guess only if the header is missing.
      const disposition = res.headers['content-disposition'] || '';
      const named = /filename="?([^";]+)"?/i.exec(disposition);
      const slug = (collectionFilter || 'All-Collections').replace(/[^A-Za-z0-9]+/g, '-');

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = named ? named[1] : `FARA-Catalogue-${slug}-${currency}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating catalogue PDF:', err);
      setActionError('Failed to generate the catalogue PDF. Please try again.');
    } finally {
      setCatalogueBusy(false);
    }
  };

  const productColumns = [
    {
      name: 'Image',
      cell: row => <img src={row.image} alt={row.title} width={60} height={60} className="rounded" style={{objectFit: 'cover'}} />,
      width: '80px'
    },
    {
      name: 'Details',
      grow: 2,
      selector: row => row.title,
      sortable: true,
      cell: row => (
        <div>
          <h4
            className="mb-1"
            dangerouslySetInnerHTML={{
              __html: highlightSearchTerm(row.title, searchTerm)
            }}
          />
          <div className="text-muted small mt-1">
            Colour: <span dangerouslySetInnerHTML={{
              __html: highlightSearchTerm(row.colour, searchTerm)
            }} />,
            Fabric: <span dangerouslySetInnerHTML={{
              __html: highlightSearchTerm(row.fabric, searchTerm)
            }} />
          </div>
        </div>
      ),
    },
    {
      name: 'SKU',
      selector: row => row.sku,
      sortable: true,
      cell: row => (
        <span dangerouslySetInnerHTML={{
          __html: highlightSearchTerm(row.sku, searchTerm)
        }} />
      )
    },
    {
      name: 'Collection',
      selector: row => row.collection,
      sortable: true,
      cell: row => (
        <span dangerouslySetInnerHTML={{
          __html: highlightSearchTerm(row.collection, searchTerm)
        }} />
      )
    },
    {
      name: 'Status',
      cell: row => (
        row.is_active
          ? <Badge color="success">Active</Badge>
          : <Badge color="secondary">Disabled</Badge>
      ),
      width: '100px'
    },
    {
      name: 'WS ($)',
      selector: row => row.wholesale_price_usd,
      right: true,
      width: '90px'
    },
    {
      name: 'WS (£)',
      selector: row => row.wholesale_price_gbp,
      right: true,
      width: '90px'
    },
    {
      name: 'WS (€)',
      selector: row => row.wholesale_price,
      right: true,
      width: '90px'
    },
    {
      name: 'Retail (€)',
      selector: row => row.retail_euro,
      right: true,
      width: '100px'
    },
    {
      name: 'Actions',
      cell: row => (
        <UncontrolledDropdown>
          <DropdownToggle caret color="primary" size="sm" outline>
            Actions
          </DropdownToggle>
          {/* Rendered into <body> so the menu isn't clipped by the table's own overflow. */}
          <DropdownMenu end container="body">
            <DropdownItem onClick={() => openEdit(row)}>
              <i className="fa fa-pencil me-2"></i>Edit
            </DropdownItem>
            <DropdownItem onClick={() => toggleStatus(row)}>
              <i className={`fa ${row.is_active ? 'fa-ban' : 'fa-check-circle'} me-2`}></i>
              {row.is_active ? 'Disable' : 'Activate'}
            </DropdownItem>
            <DropdownItem onClick={() => openHistory(row.sku)}>
              <i className="fa fa-history me-2"></i>Price History
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem className="text-danger" onClick={() => setDeleteTarget(row)}>
              <i className="fa fa-trash me-2"></i>Delete
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      width: '120px'
    }
  ];

  return (
    <div className="p-4">
      <style>
        {`
          mark {
            background-color: #fff3cd;
            color: #856404;
            padding: 2px 4px;
            border-radius: 3px;
            font-weight: bold;
          }
        `}
      </style>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Product List</h2>
        <div className="d-flex gap-2">
          <UncontrolledDropdown>
            <DropdownToggle color="dark" caret disabled={catalogueBusy}>
              {catalogueBusy
                ? <><Spinner size="sm" className="me-2" />Building catalogue…</>
                : <><i className="fa fa-book me-2"></i>Catalogue PDF</>}
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem header>Wholesale prices in</DropdownItem>
              <DropdownItem onClick={() => downloadCatalogue('EUR')}>EUR (€)</DropdownItem>
              <DropdownItem onClick={() => downloadCatalogue('USD')}>USD ($)</DropdownItem>
              <DropdownItem onClick={() => downloadCatalogue('GBP')}>GBP (£)</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
          <Button
            color="success"
            onClick={() => window.open(`${API_BASE_URL}/api/export-product-list`, '_blank')}
          >
            <i className="fa fa-file-excel-o me-2"></i>
            Export to Excel
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="alert alert-danger py-2" role="alert">{actionError}</div>
      )}

      {/* Search Section */}
      <Row className="mb-4">
        <Col md="6">
          <div className="d-flex gap-2">
            <div className="position-relative flex-grow-1">
              <Input
                type="text"
                placeholder="Search products by SKU, title, collection, color, or fabric..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="form-control"
              />
              {searchTerm && (
                <i
                  className="fa fa-search position-absolute"
                  style={{right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d'}}
                ></i>
              )}
            </div>
            <Button color="primary" onClick={handleSearch} disabled={pending}>
              {pending ? 'Searching...' : 'Search'}
            </Button>
            <Button color="secondary" onClick={handleClearSearch} disabled={pending}>
              Clear
            </Button>
          </div>
          {searchTerm && (
            <small className="text-muted mt-1 d-block">
              Searching for: "<strong>{searchTerm}</strong>"
            </small>
          )}
        </Col>
        <Col md="3">
          <Input
            type="select"
            value={collectionFilter}
            onChange={(e) => setCollectionFilter(e.target.value)}
            aria-label="Filter by collection"
          >
            <option value="">All collections</option>
            {collections.map(collection => (
              <option key={collection} value={collection}>{collection}</option>
            ))}
          </Input>
          {collectionFilter && (
            <small className="text-muted mt-1 d-block">
              Collection: <strong>{collectionFilter}</strong>
            </small>
          )}
        </Col>
        <Col md="3" className="text-end">
          <div className="d-flex flex-column align-items-end">
            <small className="text-muted">
              {filteredData.length} product{filteredData.length !== 1 ? 's' : ''} found
            </small>
            {(searchTerm || collectionFilter) && filteredData.length === 0 && (
              <small className="text-warning">
                No products match your search criteria
              </small>
            )}
          </div>
        </Col>
      </Row>

      <DataTable
        className="display dataTable no-footer"
        noHeader
        pagination
        columns={productColumns}
        data={filteredData}
        progressPending={pending}
        highlightOnHover
        striped
        responsive
        conditionalRowStyles={[
          {
            when: row => !row.is_active,
            style: { opacity: 0.55, backgroundColor: '#f8f9fa' },
          },
        ]}
      />

      {/* Edit Modal */}
      <Modal isOpen={editOpen} toggle={() => setEditOpen(!editOpen)} centered size="lg">
        <ModalHeader toggle={() => setEditOpen(false)}>
          Edit Product{editForm ? ` — ${editForm.title}` : ''}
        </ModalHeader>
        <ModalBody>
          {editForm && (
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Title</Label>
                  <Input value={editForm.title} onChange={(e) => handleEditChange('title', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>SKU</Label>
                  <Input value={editForm.sku} onChange={(e) => handleEditChange('sku', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Colour</Label>
                  <Input value={editForm.colour} onChange={(e) => handleEditChange('colour', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Fabric</Label>
                  <Input value={editForm.fabric} onChange={(e) => handleEditChange('fabric', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Collection</Label>
                  <Input value={editForm.collection} onChange={(e) => handleEditChange('collection', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>HSN Code</Label>
                  <Input value={editForm.hsn_code} onChange={(e) => handleEditChange('hsn_code', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Wholesale (USD)</Label>
                  <Input type="number" step="0.01" value={editForm.wholesale_price_usd} onChange={(e) => handleEditChange('wholesale_price_usd', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Wholesale (GBP)</Label>
                  <Input type="number" step="0.01" value={editForm.wholesale_price_gbp} onChange={(e) => handleEditChange('wholesale_price_gbp', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Wholesale (EUR)</Label>
                  <Input type="number" step="0.01" value={editForm.wholesale_price} onChange={(e) => handleEditChange('wholesale_price', e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Retail (EUR)</Label>
                  <Input type="number" step="0.01" value={editForm.retail_euro} onChange={(e) => handleEditChange('retail_euro', e.target.value)} />
                </FormGroup>
              </Col>
            </Row>
          )}
          {actionError && <div className="text-danger">{actionError}</div>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="primary" onClick={saveEdit} disabled={saving}>
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save changes'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader toggle={() => setDeleteTarget(null)}>Delete Product</ModalHeader>
        <ModalBody>
          {deleteTarget && (
            <p className="mb-0">
              Are you sure you want to permanently delete <strong>{deleteTarget.title}</strong>
              {deleteTarget.sku ? ` (${deleteTarget.sku})` : ''} and all its size variants?
              This cannot be undone.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <><Spinner size="sm" /> Deleting…</> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Price History Modal */}
      <Modal isOpen={historyOpen} toggle={() => setHistoryOpen(!historyOpen)} centered size="lg">
        <ModalHeader toggle={() => setHistoryOpen(false)}>
          Price History{historyProduct ? ` — ${historyProduct.title} (${historyProduct.sku})` : ''}
        </ModalHeader>
        <ModalBody>
          {historyLoading ? (
            <div className="text-center py-4"><Spinner size="sm" /> Loading…</div>
          ) : (
            <>
              {historyProduct && (
                <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa' }}>
                  <strong>Current (active) price:</strong>{' '}
                  WS €{historyProduct.wholesale_price ?? '—'} ·
                  Retail €{historyProduct.retail_euro ?? '—'} ·
                  WS ${historyProduct.wholesale_price_usd ?? '—'} ·
                  WS £{historyProduct.wholesale_price_gbp ?? '—'}
                  <span className="text-muted"> — used for new orders</span>
                </div>
              )}
              {historyRows.length === 0 ? (
                <p className="text-muted mb-0">No season price history recorded for this product.</p>
              ) : (
                <Table bordered striped size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Season</th>
                      <th className="text-end">Wholesale (€)</th>
                      <th className="text-end">Retail (€)</th>
                      <th className="text-end">Wholesale ($)</th>
                      <th className="text-end">Wholesale (£)</th>
                      <th>Effective From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((h, i) => (
                      <tr key={i}>
                        <td><strong>{h.season}</strong></td>
                        <td className="text-end">{h.wholesale_price ?? '—'}</td>
                        <td className="text-end">{h.retail_euro ?? '—'}</td>
                        <td className="text-end">{h.wholesale_price_usd ?? '—'}</td>
                        <td className="text-end">{h.wholesale_price_gbp ?? '—'}</td>
                        <td>{h.effective_from ? String(h.effective_from).slice(0, 10) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ProductList;
